import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { Download } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-hot-toast";
import DataTable from "../common/DataTable";
import { Close } from "../icons";
import {
  useGetCampusesQuery,
  useGetCentralInventoryCampusSideQuery,
  useLazyGetIssuedStockReportQuery,
  useGetMeQuery,
} from "../../store/api";

export default function IssuedStockReportModal({ open, onClose }) {
  const [requestedCampusId, setRequestedCampusId] = useState("");
  const [centralCampusInventoryItemId, setCentralCampusInventoryItemId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showAll, setShowAll] = useState(false);

  const { data: userData } = useGetMeQuery();
  const allowedRoutes = userData?.data?.allowedRoutes || [];
  const isCentral = allowedRoutes.includes("all_requests") || allowedRoutes.includes("all");

  const { data: campusesData } = useGetCampusesQuery(1, { skip: !isCentral });
  const campusOptions = campusesData?.data?.campuses || [];

  const { data: inventoryData } = useGetCentralInventoryCampusSideQuery(undefined, {
    skip: !open,
  });

  const itemOptions = (inventoryData?.data?.items || []).map((item) => ({
    label: item.itemName,
    value: item.id,
  }));

  const [triggerGetReport, { data: reportData, isFetching }] =
    useLazyGetIssuedStockReportQuery();

  const [triggerGetAllReport, { isFetching: isGenerating }] =
    useLazyGetIssuedStockReportQuery();

  const handleFetch = () => {
    setPage(1);
    fetchData(1);
  };

  const fetchData = (targetPage) => {
    triggerGetReport({
      page: targetPage,
      limit,
      requestedCampusId,
      centralCampusInventoryItemId,
      fromDate,
      toDate,
      showAll: showAll ? "true" : "false",
    });
  };

  useEffect(() => {
    if (open) {
      setPage(1);
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleGenerateReport = async () => {
    try {
      const response = await triggerGetAllReport({
        requestedCampusId,
        centralCampusInventoryItemId,
        fromDate,
        toDate,
        showAll: true,
      }).unwrap();

      const items = response?.data?.report || [];
      if (items.length === 0) {
        toast.error("No data available to generate report");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(16);
      
      const campusName =
        campusOptions.find((c) => c._id === requestedCampusId)?.name ||
        "All Campuses";

      doc.text(`${campusName} - Issued Stock Report`, 14, 20);
      
      const tableColumn = [
        "Campus",
        "Item",
        "Requested By",
        "Req Qty",
        "VP Qty",
        "Cent Qty",
        "Issued Qty",
        "Status",
        "Date"
      ];
      const tableRows = items.map((r) => [
        r?.campus?.name || "N/A",
        `${r?.item?.name || "N/A"}\n${r?.category?.name || ""}\n${r?.vendor?.name || ""}`,
        `${r?.requestedBy?.name || "N/A"}\n${r?.requestedBy?.email || ""}`,
        r?.requestedQty || 0,
        r?.vpModifiedQty || 0,
        r?.centralModifiedQty || 0,
        r?.issuedQty || 0,
        r?.status === "recieved" ? "Received" : "Approved",
        new Date(r?.updatedAt).toLocaleDateString("en-GB"),
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
        theme: "striped",
      });

      doc.save(`Issued_Stock_Report_${campusName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      toast.error("Failed to generate report");
    }
  };

  const rows = reportData?.data?.report || [];
  const totalPages = reportData?.data?.totalPages || 1;

  const columns = [
    { key: "campus", label: "Campus", className: "col-span-2 pl-4" },
    { key: "item", label: "Item Details", className: "col-span-3" },
    { key: "requestedBy", label: "Requested By", className: "col-span-2" },
    { key: "quantities", label: "Quantities", className: "col-span-2 text-center" },
    { key: "status", label: "Status", className: "col-span-1 text-center" },
    { key: "date", label: "Date", className: "col-span-2 text-center" },
  ];

  const renderRow = (r, idx) => (
    <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-3 border-b text-sm items-center bg-[#EEEEEE40]">
      <div className="col-span-2 truncate text-black font-medium pl-4">
        {r?.campus?.name || "N/A"}
      </div>
      <div className="col-span-3 flex items-center gap-2">
        {r?.item?.itemPicture?.secure_url && (
           <img src={r.item.itemPicture.secure_url} alt="item" className="w-10 h-10 rounded-lg object-cover shrink-0" />
        )}
        <div className="truncate text-black">
          <p className="font-semibold truncate">{r?.item?.name || "N/A"}</p>
          <p className="text-[10px] text-gray-500 truncate">{r?.category?.name} | {r?.vendor?.name}</p>
        </div>
      </div>
      <div className="col-span-2 flex items-center gap-2">
        <div className="truncate text-black">
          <p className="font-semibold truncate">{r?.requestedBy?.name || "N/A"}</p>
          <p className="text-[10px] text-gray-500 truncate">{r?.requestedBy?.email}</p>
        </div>
      </div>
      <div className="col-span-2 text-center text-[11px] text-black flex flex-col items-center">
         <p>Req: {r?.requestedQty} | VP: {r?.vpModifiedQty}</p>
         <p>Cent: {r?.centralModifiedQty} | Iss: <span className="font-bold text-green-600">{r?.issuedQty}</span></p>
      </div>
      <div className="col-span-1 text-center truncate text-black">{r?.status === "recieved" ? "Received" : "Approved"}</div>
      <div className="col-span-2 text-center text-xs text-black">
         {new Date(r?.updatedAt).toLocaleDateString("en-GB")}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={null}
      panelClassName="!max-w-[1100px] w-full md:w-[95vw] !p-0 max-h-[95vh] overflow-y-auto"
      centered={false}
    >
      <div className="w-full pb-4">
        <div className="flex items-center justify-between mb-4 px-9 pt-6">
          <div>
            <p className="text-xl leading-6 text-black font-bold">Issued Stock Report</p>
            <p className="text-sm leading-4 text-gray-500">View and generate issued stock tracking reports</p>
          </div>
          <button onClick={onClose} aria-label="close">
            <Close />
          </button>
        </div>

        <div className="border-t border-[#E5E5E5] pt-6 px-9">
          <div className="flex flex-wrap gap-4 items-end mb-6">
            {isCentral && (
              <div className="flex flex-col gap-1">
                <label className="text-xs text-black font-medium">Campus</label>
                <select
                  value={requestedCampusId}
                  onChange={(e) => setRequestedCampusId(e.target.value)}
                  className="border border-[#D9D9D9] bg-[#EEEEEE40] text-black outline-none rounded px-3 py-2 text-sm min-w-[160px]"
                >
                  <option value="">All Campuses</option>
                  {campusOptions.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="flex flex-col gap-1">
              <label className="text-xs text-black font-medium">Item</label>
              <select
                value={centralCampusInventoryItemId}
                onChange={(e) => setCentralCampusInventoryItemId(e.target.value)}
                className="border border-[#D9D9D9] bg-[#EEEEEE40] text-black outline-none rounded px-3 py-2 text-sm min-w-[160px]"
              >
                <option value="">All Items</option>
                {itemOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-black font-medium">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-[#D9D9D9] bg-[#EEEEEE40] text-black outline-none rounded px-3 py-2 text-sm min-w-[130px]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-black font-medium">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-[#D9D9D9] bg-[#EEEEEE40] text-black outline-none rounded px-3 py-2 text-sm min-w-[130px]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-black font-medium">Rows / Page</label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                disabled={showAll}
                className="border border-[#D9D9D9] bg-[#EEEEEE40] text-black outline-none rounded px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                id="showAll"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="w-4 h-4 cursor-pointer"
              />
              <label htmlFor="showAll" className="text-sm text-black font-medium cursor-pointer">
                Show All
              </label>
            </div>

            <button
              onClick={handleFetch}
              className="bg-[#6D4E8A] text-white px-6 py-2 rounded text-sm font-bold hover:bg-[#5b4074] transition-colors"
            >
              {isFetching ? "Fetching..." : "View"}
            </button>

            <button
              onClick={handleGenerateReport}
              disabled={isGenerating}
              className="flex items-center gap-2 bg-[#279B9F] text-white px-6 py-2 rounded text-sm font-bold hover:bg-[#1f7a7d] transition-colors disabled:opacity-50 ml-auto"
            >
              <Download size={16} /> {isGenerating ? "Generating..." : "Generate Report"}
            </button>
          </div>

          <div className="border border-[#E5E5E5] rounded-lg overflow-hidden overflow-x-auto">
            <div className="min-w-[900px]">
              <DataTable
              columns={columns}
              rows={rows}
              renderRow={renderRow}
              page={page}
              totalPages={totalPages}
              onNext={() => {
                const p = Math.min(page + 1, totalPages);
                setPage(p);
                fetchData(p);
              }}
              onPrev={() => {
                const p = Math.max(page - 1, 1);
                setPage(p);
                fetchData(p);
              }}
              hidePagination={showAll}
            />
            {(!rows || rows.length === 0) && !isFetching && (
              <div className="py-8 text-center text-gray-500 text-sm">
                No issued stock found for the selected filters.
              </div>
            )}
            {isFetching && (
              <div className="py-8 text-center text-gray-500 text-sm">
                Loading data...
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
